import React, { createContext, FunctionComponent, PropsWithChildren, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Environment, Stack } from '../../../server/src/models';
import { CreateStackRequest } from '../../../server/src/models/stack';
import { trpc } from '../../utils/trpc';

type ContextStateType = {
  env: Environment;
};

type ContextActionsType = {
  createStack: (req: CreateStackRequest) => void;
  startStack: (stack: Stack) => void;
  stopStack: (stack: Stack) => void;
  deleteStack: (stack: Stack) => void;
  updateStack: (stack: Stack) => void;
  deleteEnvironment: (id: string) => void;
};

const nullFunc = () => {
  throw Error('This must be overridden');
};

const initialState: ContextStateType = {
  env: null as unknown as Environment
};

const initialActions: ContextActionsType = {
  createStack: nullFunc,
  startStack: nullFunc,
  stopStack: nullFunc,
  deleteStack: nullFunc,
  updateStack: nullFunc,
  deleteEnvironment: nullFunc
};

type ContextType = {
  state: ContextStateType;
  actions: ContextActionsType;
};

const EnvironmentContext = createContext<ContextType>({
  state: initialState,
  actions: initialActions
});

const EnvironmentProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const [state, setState] = useState<ContextStateType>(initialState);
  const params = useParams();
  const envId = params['env_id'];

  // Make sure that the UI object is removed when the envId is set to null
  useEffect(() => {
    if (!envId) {
      setState({
        env: null as unknown as Environment
      });
    }
  }, [envId]);

  const utils = trpc.useContext();

  const envUpdate = trpc.env.update.useMutation({
    onSuccess: async (_data, variables) => {
      console.log('Environment updated, flushing get and list.');

      await utils.env.get.invalidate(variables.id);
      await utils.env.list.invalidate();
    }
  });

  const environmentDeleteMutation = trpc.env.delete.useMutation({
    onSuccess: async (_data, variables) => {
      console.log('Environment deleted, flushing get and list.');
      await utils.env.get.invalidate(variables);
      await utils.env.list.invalidate();
    }
  });

  const deleteEnvironment = (id: string) => {
    environmentDeleteMutation.mutate(id);
  };

  const stackStopMutation = trpc.stack.stop.useMutation({
    onSuccess: (data, variables) => {
      console.log(`Stack ${variables} stopped, updating environment.`);
      if (state.env) {
        const currEnv = Object.assign({}, state.env);
        currEnv.stack.stackState = data;
        currEnv.stack.updatedAt = new Date().toISOString();
        envUpdate.mutate(currEnv);
      }
    }
  });

  const stopStack = (stack: Stack) => {
    stackStopMutation.mutate(stack.id!);
  };

  const stackStartMutation = trpc.stack.start.useMutation({
    onSuccess: (data, variables) => {
      console.log(`Stack ${variables} started, updating environment.`);
      if (state) {
        const currEnv = Object.assign({}, state.env);
        currEnv.stack.stackState = data;
        currEnv.stack.updatedAt = new Date().toISOString();
        envUpdate.mutate(currEnv);
      }
    }
  });

  const stackDeleteMutation = trpc.stack.delete.useMutation({
    onSuccess: () => {
      console.log('Stack deleted.');
      if (state) {
        const currEnv = Object.assign({}, state.env);
        currEnv.stack.stackState = 'DELETED';
        currEnv.stack.id = undefined;
        currEnv.stack.updatedAt = new Date().toISOString();
        envUpdate.mutate(currEnv);
      }
    }
  });

  const deleteStack = (stack: Stack) => {
    stackDeleteMutation.mutate(stack.id!);
  };

  const startStack = (stack: Stack) => {
    stackStartMutation.mutate(stack.id!);
  };

  const updateStack = (stack: Stack) => {
    stackUpdateMutation.mutate(stack);
  };

  const stackCreateMutation = trpc.stack.create.useMutation({
    onSuccess: (data) => {
      console.log('Stack created, updating environment.');
      if (state) {
        const currEnv = Object.assign({}, state.env);
        currEnv.stack.stackState = 'ACTIVE';
        currEnv.stack.createdAt = new Date().toISOString();
        currEnv.stack.id = data.id;
        envUpdate.mutate(currEnv);
      }
    }
  });

  const stackUpdateMutation = trpc.stack.update.useMutation({
    onSuccess: (data) => {
      console.log('Stack updated, updating environment.');
      if (state) {
        const currEnv = Object.assign({}, state.env);
        currEnv.stack.stackState = data;
        currEnv.stack.updatedAt = new Date().toISOString();
        envUpdate.mutate(currEnv);
      }
    }
  });

  const createStack = (req: CreateStackRequest) => {
    stackCreateMutation.mutate(req);
  };

  const env = trpc.env.get.useQuery(envId!, {
    enabled: !!envId,
    refetchInterval: 90000
  });

  useEffect(() => {
    if (env.isSuccess && env.data) {
      setState({ env: env.data });
    } else {
      setState({ env: null as unknown as Environment });
    }
  }, [env.isSuccess, env.data]);

  return (
    <EnvironmentContext.Provider
      value={{
        state,
        actions: {
          createStack,
          startStack,
          stopStack,
          deleteStack,
          updateStack,
          deleteEnvironment
        }
      }}
    >
      {children}
    </EnvironmentContext.Provider>
  );
};

export { EnvironmentContext, EnvironmentProvider };
